import { Component, inject, OnDestroy, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import { GroupButton } from '@componentes/public-button-group/public-button-group.component';
import { ADTColumns } from 'angular-datatables/src/models/settings';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import {
	AgrupamentoEspecial,
	DadosAgrupamento,
	FormularioRelatorio,
	INPUTS_RELATORIOS,
	InstanciaRelatorio,
	PeriodosLabel,
	RelatorioVECResponse,
} from '../../../interfaces';
import { RelatoriosService } from '../../../services';
import { DateTimeUtils } from '@utils/datetime-util';
import {
	exportarCSV,
	exportarPDF,
	exportarTXT,
	exportarXLSX,
} from '@home/submodulos/dados-meteorologicos/utils/exportacao-tabelas';
import { PublicTableComponent } from '@componentes/public-table/public-table.component';
import { corrigeDuplicacaoNome } from '@home/submodulos/dados-meteorologicos/utils';
import {
	enumAsSelectOptions,
	isNotNuloOuUndefined,
	isNuloOuUndefined,
	renderCoordenada,
	renderNumeroBR,
} from '@utils';
import { Select } from '@layout/interfaces/select';
import { configsColunasCoordenadas } from '../../../utils/configs-colunas-exportacao';

type RelatorioVECWithMunicipioPosto = RelatorioVECResponse & {
	municipioPosto?: string;
};

@Component({
	selector: 'seira-tabela-vec',
	templateUrl: './tabela-vec.component.html',
	styleUrls: ['./tabela-vec.component.scss'],
})
export class TabelaVecComponent implements OnDestroy, InstanciaRelatorio {
	@ViewChild('tabelaVec', { static: false })
	tabela: PublicTableComponent;
	inputs = inject(INPUTS_RELATORIOS);
	_destroyed = new Subject();

	tituloColunaAgrupamento = 'Agrupamento';
	descricaoRelatorio =
		'Os valores extremos de pluviometria se referem aos valores máximos de chuva registrados de um determinado período, local ou região.';

	carregando = true;
	disabledSelectList = false;
	private colunasIniciais = [
		'Município/Posto',
		'Latitude',
		'Longitude',
		'Altitude',
	];
	colunasVEC: ADTColumns[] = [];
	resultados: any[] = [];
	colunasSelecionadas: string[] = [];

	tituloTabela = '';
	periodoAgrupamento = '';

	opcoesColunas = [
		{ name: 'Município/Posto', isFixed: true },
		{ name: 'Latitude', isFixed: false },
		{ name: 'Longitude', isFixed: false },
		{ name: 'Altitude', isFixed: false },
		{ name: 'Microrregião', isFixed: false },
		{ name: 'Mesorregião', isFixed: false },
		{ name: 'Região Pluviométrica', isFixed: false },
		{ name: 'Bacia/Sub-bacia', isFixed: false },
		/*	{ name: 'Sub-bacia', isFixed: false },*/
	];
	botoesDeExportacao: GroupButton[] = [
		{
			label: '.pdf',
			size: 'small',
			icon: 'ph-file-pdf',
			onClick: () =>
				exportarPDF({
					resultados: this.resultados,
					tabela: this.tabela,
					periodoAgrupamento: this.periodoAgrupamento,
					tituloTabela: this.tituloTabela,
					colunasAEsquerda: ['MUNICIPIO', 'MUNICIPIO_POSTO'].includes(
						this._agrupamentoAtual
					)
						? [0]
						: [1],
				}),
		},
		{
			label: '.csv',
			size: 'small',
			icon: 'ph-file-csv',
			onClick: () =>
				exportarCSV({
					tabela: this.tabela,
					periodoAgrupamento: this.periodoAgrupamento,
					tituloTabela: this.tituloTabela,
				}),
		},
		{
			label: '.txt',
			size: 'small',
			icon: 'ph-file-text',
			onClick: () =>
				exportarTXT({
					tabela: this.tabela,
					periodoAgrupamento: this.periodoAgrupamento,
					titulo: this.tituloTabela,
				}),
		},
		{
			label: '.xlsx',
			size: 'small',
			icon: 'ph-file-xls',
			onClick: () =>
				exportarXLSX({
					tabela: this.tabela,
					periodoAgrupamento: this.periodoAgrupamento,
					tituloTabela: this.tituloTabela,
					configsColunas: [
						...configsColunasCoordenadas,
						{ coluna: 'precipitacaoMaxima', casasDecimais: 1 },
					],
					colunasAEsquerda:
						this._agrupamentoAtual === 'MUNICIPIO' ||
						this._agrupamentoAtual === 'MUNICIPIO_POSTO'
							? [0]
							: [1],
				}),
		},
	];
	agrupamento: Select<string>[];
	constructor(
		private relatoriosService: RelatoriosService,
		private toastrService: ToastrService
	) {
		this.colunasSelecionadas = this.opcoesColunas
			.filter(option => this.colunasIniciais.includes(option.name))
			.map(option => option.name);
		this.agrupamento = enumAsSelectOptions(AgrupamentoEspecial);
		this.setValidators();
	}

	ngOnDestroy(): void {
		this.inputs.form.get(FormularioRelatorio.DATA_INICIO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.DATA_FIM)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.PERIODO_BUSCA)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.clearValidators();
	}

	setValidators() {
		this.inputs.form
			.get(FormularioRelatorio.DATA_INICIO)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.DATA_FIM)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.PERIODO_BUSCA)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.AGRUPAMENTO)
			?.setValidators(Validators.required);
	}

	getDataFromForm(data: FormularioRelatorio) {
		return this.inputs.form.get(data);
	}

	setColunasSelecionadas(colunas: string[]) {
		this.colunasSelecionadas = colunas;
		this.gerarRelatorio();
	}

	get _agrupamentoAtual() {
		return this.getDataFromForm(FormularioRelatorio.AGRUPAMENTO)?.value;
	}

	gerarRelatorio() {
		const diaInicio = this.getDataFromForm(FormularioRelatorio.DATA_INICIO)
			?.value;
		const diaFim = this.getDataFromForm(FormularioRelatorio.DATA_FIM)?.value;

		this.periodoAgrupamento = this.getDataFromForm(
			FormularioRelatorio.PERIODO_BUSCA
		)?.value as PeriodosLabel;

		if (
			!diaInicio ||
			!diaFim ||
			!this._agrupamentoAtual ||
			!this.periodoAgrupamento ||
			this.inputs.form.invalid
		) {
			return;
		}

		this.carregando = true;
		this.inputs.setLoading(true);

		this.colunasVEC = [];
		this.resultados = [];
		const periodoTitulo = DateTimeUtils.formatarDataPeriodo(
			diaInicio,
			diaFim,
			this.periodoAgrupamento
		);

		this.tituloTabela = `Valores extremos de pluviometria - ${periodoTitulo}`;

		const dados: DadosAgrupamento = {
			diaInicio,
			diaFim,
			agrupamento: this._agrupamentoAtual,
			periodoAgrupamento: this.periodoAgrupamento.toUpperCase(),
		};

		this.relatoriosService.buscarRelatorioVECPorAgrupamento(dados).subscribe({
			next: ndcAgrupados => {
				this.relatoriosService.atualizaVisibilidadeBotoesExportacao(
					ndcAgrupados
				);
				const ndcsComMunicipioPosto: RelatorioVECWithMunicipioPosto[] = [
					...ndcAgrupados,
				];
				ndcsComMunicipioPosto.forEach(ndc => {
					if (this._agrupamentoAtual === 'MUNICIPIO_POSTO') {
						ndc.municipioPosto = ndc.nome;
						ndc.nome = ndc.municipioPosto;
					} else if (this._agrupamentoAtual === 'MUNICIPIO') {
						ndc.municipioPosto = corrigeDuplicacaoNome(
							ndc.nome + (isNuloOuUndefined(ndc.posto) ? '' : ' / ' + ndc.posto)
						);
						ndc.nome = ndc.municipioPosto;
					} else if (isNotNuloOuUndefined(ndc.posto)) {
						ndc.municipioPosto = corrigeDuplicacaoNome(
							ndc.municipio +
								(isNuloOuUndefined(ndc.posto) ? '' : ' / ' + ndc.posto)
						);
					}
				});
				this.resultados = this.padronizarResultado(
					ndcsComMunicipioPosto,
					this._agrupamentoAtual
				);
				this.atualizarTitulosTabela();
				this.altualizarColunasTabelaVEC();
			},
			error: () => {
				this.toastrService.error(
					'Erro ao gerar tabela de Volumes Extremos de Chuva, tente novamente'
				);
				this.carregando = false;
				this.inputs.setLoading(false);
			},
			complete: () => {
				this.inputs.setLoading(false);
				this.carregando = false;
			},
		});
	}

	padronizarResultado(
		resultados: RelatorioVECWithMunicipioPosto[],
		agrupamento: string
	) {
		return resultados.map(item => {
			let dataFormatada = '';
			if (!item.dataColeta) {
				dataFormatada = '-';
			} else {
				dataFormatada = this.formatarDataColeta(item.dataColeta);
			}

			const base = {
				nome:
					agrupamento === 'MUNICIPIO_POSTO'
						? corrigeDuplicacaoNome(item.nome)
						: item.nome,
				municipio: item.municipio || '-',
				municipioPosto: item.municipioPosto
					? corrigeDuplicacaoNome(item.municipioPosto)
					: undefined,
				posto: item.posto || '-',
				dataColeta: dataFormatada,
				precipitacaoMaxima: item.precipitacaoMaxima,
				latitude: item.latitude,
				longitude: item.longitude,
				altitude: item.altitude,
			};

			if (agrupamento === 'MUNICIPIO_POSTO') {
				return {
					...base,
					microrregiao: item.microrregiao || '-',
					mesorregiao: item.mesorregiao || '-',
					regiao: item.regiao || '-',
					bacia: item.bacia || '-',
				};
			}
			return base;
		});
	}

	private formatarDataColeta(dataColeta: string | number) {
		const [data] = String(dataColeta).split('T');
		const [ano, mes, dia] = data.split('-');
		return dia && mes && ano ? `${dia}/${mes}/${ano}` : '-';
	}

	altualizarColunasTabelaVEC() {
		const coordenadas = [
			{
				data: 'latitude',
				title: 'Latitude',
				type: 'number',
				className: 'text-center',
				render: (valor: string) => renderCoordenada(valor),
			},
			{
				data: 'longitude',
				title: 'Longitude',
				type: 'number',
				className: 'text-center',
				render: (valor: string) => renderCoordenada(valor),
			},
			{
				data: 'altitude',
				title: 'Altitude',
				type: 'number',
				className: 'text-center',
				render: (valor: string) => renderCoordenada(valor, 1),
			},
		];
		const dataEPrecipitacao = [
			{
				data: 'dataColeta',
				title: 'Data',
				type: 'string',
				className: 'text-center',
			},
			{
				data: 'precipitacaoMaxima',
				title: 'Pluviometria Máxima (mm)',
				className: 'text-center',
				render: (valor: string) => renderNumeroBR(valor),
			},
		];
		const agrupamento = this.getDataFromForm(FormularioRelatorio.AGRUPAMENTO)
			?.value;

		if (agrupamento === 'MUNICIPIO_POSTO') {
			this.disabledSelectList = false;
			const todasColunasDisponiveis = [
				{
					data: 'nome',
					title: 'Município/Posto',
					type: 'string',
					className: 'text-left',
					render: (valor: string) => {
						return isNuloOuUndefined(valor) ? '-' : valor;
					},
				},
				{
					data: 'id',
					title: 'Id',
					type: 'number',
					className: 'text-center',
				},
				...coordenadas,
				{
					data: 'microrregiao',
					title: 'Microrregião',
					type: 'string',
					className: 'text-center',
				},
				{
					data: 'mesorregiao',
					title: 'Mesorregião',
					type: 'string',
					className: 'text-center',
				},
				{
					data: 'regiao',
					title: 'Região Pluviométrica',
					type: 'string',
					className: 'text-center',
				},
				{
					data: 'bacia',
					title: 'Bacia/Sub-bacia',
					type: 'string',
					className: 'text-center',
				},
			];

			const colunasParaExibir = todasColunasDisponiveis.filter(coluna =>
				this.colunasSelecionadas.includes(coluna.title)
			);
			this.colunasVEC = [
				colunasParaExibir[0],
				...dataEPrecipitacao,
				...colunasParaExibir.slice(1),
			];
		} else if (agrupamento === 'MUNICIPIO') {
			this.disabledSelectList = true;
			this.colunasVEC = [
				{
					data: 'municipioPosto',
					title: 'Município/Posto',
					type: 'string',
					className: 'text-left',
					render: (valor: string) => {
						return isNuloOuUndefined(valor) ? '-' : valor;
					},
				},
				...dataEPrecipitacao,
				...coordenadas,
			];
		} else {
			this.disabledSelectList = true;
			this.colunasVEC = [
				{
					data: 'nome',
					title: this.tituloColunaAgrupamento,
					type: 'string',
					className: 'text-center',
				},
				{
					data: 'municipioPosto',
					title: 'Município/Posto',
					type: 'string',
					className: 'text-left',
					render: (valor: string) => {
						return isNuloOuUndefined(valor) ? '-' : valor;
					},
				},
				...dataEPrecipitacao,
				...coordenadas,
			];
		}
	}

	atualizarTitulosTabela() {
		const agrupamento = this.getDataFromForm(FormularioRelatorio.AGRUPAMENTO)
			?.value;
		this.tituloColunaAgrupamento =
			AgrupamentoEspecial[agrupamento as keyof typeof AgrupamentoEspecial];
	}
}
